import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import BreakingNews from '../components/BreakingNews';
import Footer from '../components/Footer';
import { Calendar, Eye, FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getImageUrl, getPdfUrl } from '../utils/imageHelper';
import { formatDateWithFallback } from '../utils/dateFormatter';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=1000&fit=crop';

const EPapersPage = () => {
  const [epapers, setEpapers] = useState([]);
  const [loading, setLoading] = useState(true);

  const getFileUrl = (url) => getPdfUrl(url, '');

  useEffect(() => {
    fetchEpapers();
  }, []);

  const fetchEpapers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/epapers`);
      setEpapers(response.data);
    } catch (error) {
      console.error('Failed to fetch e-papers:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEpaper = (pdfUrl) => {
    const fullUrl = getFileUrl(pdfUrl);
    if (fullUrl) {
      window.open(fullUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <BreakingNews />

      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                ই-পেপার
              </h1>
              <p className="text-xl text-gray-600">E-Paper Archive</p>
              <p className="text-gray-500 mt-2">Read today's newspaper and browse our complete archive</p>
            </div>
            <a href="/epaper/search">
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                <Calendar className="w-4 h-4 mr-2" />
                Search Archive
              </Button>
            </a>
          </div>
        </div>

        {/* E-Papers Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading e-papers...</p>
          </div>
        ) : epapers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {epapers.map((epaper) => (
              <div
                key={epaper.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                {/* E-Paper Preview */}
                <div className="relative h-96 bg-gray-100 flex items-center justify-center border-b border-gray-200">
                  {epaper.thumbnail_url ? (
                    <img
                      src={getImageUrl(epaper.thumbnail_url)}
                      alt={epaper.title}
                      className="w-full h-full object-contain"
                      onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                    />
                  ) : (
                    <div className="text-center">
                      <FileText className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-400 font-medium">PDF Document</p>
                    </div>
                  )}
                </div>

                {/* E-Paper Info */}
                <div className="p-5">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {epaper.title || 'Tripura Darpan'}
                  </h3>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDateWithFallback(epaper.date, epaper.created_at)}</span>
                    </div>
                    {epaper.views !== undefined && (
                      <div className="flex items-center space-x-1">
                        <Eye className="w-4 h-4" />
                        <span>{epaper.views} views</span>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => openEpaper(epaper.pdf_url)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Read Now
                    </Button>
                    <Button
                      onClick={() => window.open(getFileUrl(epaper.pdf_url), '_blank')}
                      variant="outline"
                      className="border-red-600 text-red-600 hover:bg-red-50"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-lg">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No E-Papers Available
            </h3>
            <p className="text-gray-500">Check back soon for the latest editions</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default EPapersPage;
